import type {
  WebLiveChannelCategoryListItemOutputDto,
  WebLiveChannelListItemTagOutputDto,
} from '@bldsoft/ovp-api-types'
import type {
  EpgInfo,
  EpgInfoDto,
  IProductionCountry,
  LiveChannel,
  LiveChannelCategory,
  LiveChannelRegion,
  TimeRange,
  TvEpg,
  TvEpgApiDto,
} from '@setplex/tria-api'
import { MediaTypes } from '@setplex/tria-api'
import { ONE_MINUTE_MS, ONE_SECOND_MS } from '../../constants/time'
import { format as formatCollection } from '../collections/format'
import type {
  ApiCatchUp,
  ApiLiveChannel,
  ApiLiveChannelCategory,
  ApiLiveChannelGlobalSearchItem,
  ApiLiveChannelListItem,
  ApiLiveChannelRegion,
  ApiLiveChannelSuggestionItem,
  ApiLiveRewind,
  ApiTimeshift,
  IApiProductionCountry,
} from './index.h'

const DISABLED_TIMESHIFT = { enabled: false, shift: 0 } as const
const DISABLED_CATCH_UP = {
  enabled: false,
  duration: 0,
  mode: 'HOURLY',
} as const
const DISABLED_LIVE_REWIND = { enabled: false, duration: 0 } as const

export const narrowCatchUpMode = (
  mode: string | undefined
): 'HOURLY' | 'EPG' => (mode === 'EPG' ? 'EPG' : 'HOURLY')

const formatTimeshift = (
  timeshift: ApiTimeshift | undefined
): LiveChannel['timeshift'] => {
  if (!timeshift) {
    return undefined
  }

  if (!timeshift.enabled) {
    return DISABLED_TIMESHIFT
  }

  return { enabled: true, shift: timeshift.shift ?? 0 }
}

const formatCatchUp = (
  catchUp: ApiCatchUp | undefined
): LiveChannel['catchUp'] => {
  if (!catchUp) {
    return undefined
  }

  if (!catchUp.enabled) {
    return DISABLED_CATCH_UP
  }

  return {
    enabled: true,
    duration: catchUp.duration ?? 0,
    mode: narrowCatchUpMode(catchUp.mode),
  }
}

const formatLiveRewind = (
  liveRewind: ApiLiveRewind | undefined
): LiveChannel['liveRewind'] => {
  if (!liveRewind?.enabled) {
    return DISABLED_LIVE_REWIND
  }

  return { enabled: true, duration: liveRewind.duration ?? 0 }
}

const formatFromListItem = ({
  id,
  number,
  title,
  collections,
  productionCountries,
  description,
  ageRating,
  logo,
  banner,
  channelEpgId,
  channelOwnerId,
  isFavorite,
  metaDescription,
  metaTitle,
  tags,
  catchUp,
  liveRewind,
  timeshift,
}: ApiLiveChannelListItem): LiveChannel => ({
  id,
  number,
  title,
  collections: collections.length ? collections.map(formatCollection) : [],
  productionCountries: productionCountries.length
    ? productionCountries.map(formatProductionCountry)
    : [],
  description,
  ageRating,
  logo,
  banner,
  channelEpgId,
  channelOwnerId,
  isFavorite,
  contentType: MediaTypes.LIVE_CHANNEL,
  metaDescription: metaDescription ?? '',
  metaTitle: metaTitle ?? '',
  categories: tags.length ? tags.map(formatLiveChannelCategory) : [],
  catchUp: formatCatchUp(catchUp),
  liveRewind: formatLiveRewind(liveRewind),
  timeshift: formatTimeshift(timeshift),
})

const formatFromGlobalSearch = ({
  id,
  number,
  title,
  collections,
  productionCountries,
  description,
  ageRating,
  logo,
  banner,
  channelEpgId,
  channelOwnerId,
  isFavorite,
  tags,
  catchUp,
  liveRewind,
  timeshift,
}: ApiLiveChannelGlobalSearchItem): LiveChannel => ({
  id,
  number,
  title,
  collections: collections.length ? collections.map(formatCollection) : [],
  productionCountries: productionCountries.length
    ? productionCountries.map(formatProductionCountry)
    : [],
  description,
  ageRating,
  logo,
  banner,
  channelEpgId,
  channelOwnerId,
  isFavorite,
  contentType: MediaTypes.LIVE_CHANNEL,
  metaDescription: '',
  metaTitle: '',
  categories: tags.length ? tags.map(formatLiveChannelCategory) : [],
  catchUp: formatCatchUp(catchUp),
  liveRewind: formatLiveRewind(liveRewind),
  timeshift: formatTimeshift(timeshift),
})

const formatFromSuggestionItem = (
  channel: ApiLiveChannelSuggestionItem
): LiveChannel => formatFromGlobalSearch({ ...channel, isFavorite: false })

const isSuggestionItem = (
  channel: ApiLiveChannel
): channel is ApiLiveChannelSuggestionItem => !('isFavorite' in channel)

export const format = (channel: ApiLiveChannel): LiveChannel => {
  if ('metaDescription' in channel) {
    return formatFromListItem(channel)
  }

  if (isSuggestionItem(channel)) {
    return formatFromSuggestionItem(channel)
  }

  return formatFromGlobalSearch(channel)
}

export const formatLiveChannelCategory = (
  category: ApiLiveChannelCategory
): LiveChannelCategory => {
  if ('channelIds' in category) {
    const listCategory = category as WebLiveChannelCategoryListItemOutputDto

    return {
      id: listCategory.id,
      title: listCategory.title || '',
      description: listCategory.description,
      horizontalImage: listCategory.horizontalImage,
      channelIds: [...listCategory.channelIds],
    }
  }

  const tagCategory = category as WebLiveChannelListItemTagOutputDto

  return {
    id: tagCategory.id,
    title: tagCategory.title || '',
  }
}

export const formatProductionCountry = ({
  id,
  name,
}: IApiProductionCountry): IProductionCountry => ({
  id,
  name: name || '',
})

export const formatLiveChannelRegion = ({
  id,
  name,
  alias,
}: ApiLiveChannelRegion): LiveChannelRegion => ({
  id,
  name: name || '',
  alias: alias || '',
})

/**
 * Function truncates the seconds and adds zeros instead of seconds
 * 1724285519000 (in ISO '2024-08-22T00:11:59Z') will be 1724285460000 ('2024-08-22T00:11:00Z')
 * @param date
 */
export const setZeroSecondsMs = (date: Date | number): number => {
  const curDateNumber = typeof date === 'number' ? date : Number(date)
  const minutes = Math.trunc(curDateNumber / ONE_MINUTE_MS)
  return minutes * ONE_MINUTE_MS
}

/**
 * Function formats date f.e. '2024-08-22T00:00:00Z' to timestamp in milliseconds (1724284800000)
 * @param item
 * @param channelEpgId
 */
export const formatEPGProgram = (
  item: EpgInfoDto,
  channelEpgId: string
): EpgInfo => {
  const start = setZeroSecondsMs(+new Date(item.start))
  const stop = setZeroSecondsMs(+new Date(item.stop))

  return {
    id: `${channelEpgId}${start}${stop}`,
    channelEpgId,
    name: item.title,
    description: item?.description !== 'N/A' ? item.description : '',
    startMs: start,
    stopMs: stop,
    actualStartMs: start,
    actualStopMs: stop,
    actualStartSec: start / ONE_SECOND_MS,
    actualStopSec: stop / ONE_SECOND_MS,
    durationMs: stop - start,
    actualDurationMs: stop - start,
    subtitle: item?.subtitle !== 'N/A' ? item.subtitle : undefined,
    categories: item.categories,
    icon: item?.icon !== 'N/A' ? item.icon : undefined,
    date: item?.date !== 'N/A' ? item.date : undefined,
    rating: item?.rating !== 'N/A' ? item.rating : undefined,
    directors: item.directors,
    actors: item.actors,
    title: item?.title,
    episode: item?.episode !== 'N/A' ? item.episode : undefined,
    isEmpty: item?.isEmpty || false,
  }
}

const generateEmptyProgram = (
  start: number,
  stop: number,
  channelEpgId: string
): EpgInfo => ({
  id: `${channelEpgId}${start}${stop}`,
  channelEpgId,
  name: undefined,
  description: undefined,
  isEmpty: true,
  startMs: start,
  stopMs: stop,
  actualStartMs: start,
  actualStopMs: stop,
  actualStartSec: start / ONE_SECOND_MS,
  actualStopSec: stop / ONE_SECOND_MS,
  durationMs: stop - start,
  actualDurationMs: stop - start,
  categories: [],
  directors: [],
  actors: [],
})

const generateEmptyPrograms = ({
  start,
  end,
  channelEpgId,
}: {
  start: number
  end: number
  channelEpgId: string
}): EpgInfo => generateEmptyProgram(start, end, channelEpgId)

export const fillGaps = (programs: EpgInfo[], period: TimeRange): EpgInfo[] => {
  const ACCEPTABLE_GAP = 5 * ONE_MINUTE_MS // 5 minutes gap is acceptable

  const tryToGenerateGap = ({
    start,
    end,
    channelEpgId,
  }: {
    start: number
    end: number
    channelEpgId: string
  }): EpgInfo | undefined => {
    if (start >= end) return
    return generateEmptyProgram(start, end, channelEpgId)
  }

  return programs
    .sort((a, b) => a.startMs - b.startMs)
    .reduce((filled: EpgInfo[], item, index, originalArr) => {
      if (index === 0) {
        const emptySlot = tryToGenerateGap({
          start: period.start,
          end: item.startMs,
          channelEpgId: item.channelEpgId,
        })
        if (emptySlot) filled.push(emptySlot)
        filled.push(item)
      } else {
        const prevProgram = filled[filled.length - 1]
        const gapDuration = item.startMs - prevProgram.stopMs

        if (gapDuration < ACCEPTABLE_GAP) {
          const extendedProgram = {
            ...prevProgram,
            stopMs: item.startMs,
            actualStopMs: item.startMs,
            actualStopSec: item.startMs / ONE_SECOND_MS,
            durationMs: item.startMs - prevProgram.startMs,
            actualDurationMs: item.startMs - prevProgram.startMs,
          }
          filled[filled.length - 1] = extendedProgram
        } else {
          const gap = tryToGenerateGap({
            start: prevProgram.stopMs,
            end: item.startMs,
            channelEpgId: item.channelEpgId,
          })
          if (gap) filled.push(gap)
        }
        filled.push(item)
      }

      if (index === originalArr.length - 1) {
        const finalGapDuration = period.end - item.stopMs

        if (finalGapDuration >= ACCEPTABLE_GAP) {
          const endEmptySlot = tryToGenerateGap({
            start: item.stopMs,
            end: period.end,
            channelEpgId: item.channelEpgId,
          })
          if (endEmptySlot) {
            filled.push(endEmptySlot)
          }
        } else if (finalGapDuration > 0) {
          const extendedLastProgram = {
            ...item,
            stopMs: period.end,
            actualStopMs: period.end,
            actualStopSec: period.end / ONE_SECOND_MS,
            durationMs: period.end - item.startMs,
            actualDurationMs: period.end - item.startMs,
          }
          filled[filled.length - 1] = extendedLastProgram
        }
      }

      return filled
    }, [])
}

const updateProgram = (
  program: EpgInfo,
  fields: {
    startMs?: number
    stopMs?: number
    actualStartMs?: number
    actualStopMs?: number
  }
) => {
  const newStart = fields.startMs || program.startMs
  const newStop = fields.stopMs || program.stopMs
  const newActualStartMs = fields.actualStartMs || program.actualStartMs
  const newActualStopMs = fields.actualStopMs || program.actualStopMs

  return {
    ...program,
    startMs: newStart,
    stopMs: newStop,
    durationMs: newStop - newStart,
    actualStartMs: newActualStartMs,
    actualStopMs: newActualStopMs,
    actualStartSec: newActualStartMs / ONE_SECOND_MS,
    actualStopSec: newActualStopMs / ONE_SECOND_MS,
    actualDurationMs: newActualStopMs - newActualStartMs,
  }
}

/**
 * Function cuts program`s start or stop value if it is bigger than start or end value of epg requested period
 * @param sortedEpgArray
 * @param period
 */
export const cutStartEndValues = (
  sortedEpgArray: EpgInfo[],
  period: TimeRange
): EpgInfo[] =>
  sortedEpgArray.map((program, index) => {
    if (index === 0) return updateProgram(program, { startMs: period.start })
    if (index === sortedEpgArray.length - 1)
      return updateProgram(program, { stopMs: period.end })
    return program
  })

const sortAndValidatePrograms = (
  programs: EpgInfo[],
  period: TimeRange
): EpgInfo[] => {
  const filledGaps = fillGaps(programs, period)
  const sorted = filledGaps.sort((a, b) => a.startMs - b.startMs)

  return cutStartEndValues(sorted, period)
}

export const formatEPGArray = (
  channelEpgId: string,
  channelProgramArr: EpgInfoDto[]
): EpgInfo[] =>
  channelProgramArr
    .filter((item) => item.title && item.description && item.start && item.stop)
    .map((item) => formatEPGProgram(item, channelEpgId))
    .filter((item) => item.startMs <= item.stopMs)

/**
 * Function adds 'empty' programs (gaps) if there is no epg for the period of time
 * @param channelEpgId
 * @param channelProgramArr
 * @param period
 */
const formatAndValidateEPGArray = (
  channelEpgId: string,
  channelProgramArr: EpgInfoDto[],
  period: TimeRange
): EpgInfo[] => {
  const formatted = formatEPGArray(channelEpgId, channelProgramArr)

  return sortAndValidatePrograms(formatted, period)
}

export const formatEPGPrograms = (
  programsCollection: TvEpgApiDto,
  channels: LiveChannel[] = [],
  period: TimeRange
): TvEpg => {
  const formattedProgramsAndGaps: TvEpg = {}

  Object.keys(programsCollection).forEach((epdId) => {
    const channel = channels.find(({ channelEpgId }) => channelEpgId === epdId)
    const existingPrograms = programsCollection[epdId]
    const isExistingToFormatting =
      existingPrograms?.length > 0 && Boolean(channel)

    formattedProgramsAndGaps[epdId] = isExistingToFormatting
      ? formatAndValidateEPGArray(epdId, existingPrograms, period)
      : []

    if (!formattedProgramsAndGaps[epdId].length) {
      formattedProgramsAndGaps[epdId] = [
        generateEmptyPrograms({
          start: period.start,
          end: period.end,
          channelEpgId: channel?.channelEpgId || '',
        }),
      ]
    }
  })
  return formattedProgramsAndGaps
}
